import { BatteryFull, BatteryWarning, BellOff, PlugZap } from 'lucide-react';
import React, { memo, useMemo } from 'react';

import ArrowTooltip from '@/components/custom-ui/customTooltip';

type StoreStatusType = 'needStock' | 'canTransfer' | 'noTrans' | 'overStock';

interface StoreStatusProps {
  status: StoreStatusType;
  store: string;
}

// Define status mapping outside component for better memory usage
const STATUS_CONFIG: Record<
  StoreStatusType,
  {
    labelTemplate: string;
    bg: string;
    text: string;
    icon: React.ReactNode;
  }
> = {
  needStock: {
    labelTemplate: '%s : 🚨 يحتاج رصيد 😱 🆘',
    bg: 'bg-red-100/80 dark:bg-red-950/80',
    text: 'text-red-800 dark:text-red-200',
    icon: <BatteryWarning className="h-5 w-5 rotate-90" />,
  },
  canTransfer: {
    labelTemplate: '%s : 🔌 ⚡️ 🔋 التحويل من مخزن 🔄 🔀 ⚡️',
    bg: 'bg-yellow-100/80 dark:bg-yellow-950/80',
    text: 'text-yellow-800 dark:text-yellow-200',
    icon: <PlugZap className="h-5 w-5 rotate-90" />,
  },
  noTrans: {
    labelTemplate: '%s : 🚫 🤷‍♂️ 🥱 لا يوجد حركة 😴 💤 🌙',
    bg: 'bg-gray-100/80 dark:bg-gray-900/80',
    text: 'text-gray-800 dark:text-gray-300',
    icon: <BellOff className="h-5 w-5 rotate-90" />,
  },
  overStock: {
    labelTemplate: '%s : 💰 🤑 🎉 رصيد زيادة 🔥 💸',
    bg: 'bg-green-200/30 dark:bg-green-400/30',
    text: 'text-green-700 dark:text-green-100',
    icon: <BatteryFull className="h-5 w-5 rotate-90" />,
  },
};

/**
 * StoreStatus - A component to display status indicators for store inventory
 *
 * Optimized for performance with:
 * - Memoization of the entire component
 * - useMemo for derived status values
 * - Static configuration object outside component
 * - TypeScript type safety
 */
const StoreStatus = memo(({ status, store }: StoreStatusProps) => {
  // Safely handle unknown status by defaulting to noTrans
  const safeStatus = useMemo(
    () =>
      Object.keys(STATUS_CONFIG).includes(status)
        ? status
        : ('noTrans' as StoreStatusType),
    [status],
  );

  // Compute label and configuration only when dependencies change
  const { label, config } = useMemo(() => {
    const statusConfig = STATUS_CONFIG[safeStatus];
    return {
      label: statusConfig.labelTemplate.replace('%s', store),
      config: statusConfig,
    };
  }, [safeStatus, store]);

  return (
    <ArrowTooltip color="primary" msg={label}>
      <div
        className={`flex h-8 w-8 items-center justify-between rounded-md border border-primary px-1 font-roboto text-sm font-bold ${config.bg} ${config.text}`}
      >
        {config.icon}
      </div>
    </ArrowTooltip>
  );
});

// Add displayName for better debugging in React DevTools
StoreStatus.displayName = 'StoreStatus';

export default StoreStatus;
